import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { listingService, requestService, notificationService } from '../services';

function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    activeListings: 0,
    pendingRequests: 0,
    completedDonations: 0,
    totalImpact: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // TEMPORARY WORKAROUND: Set default user for testing if not already set
  useEffect(() => {
    if (!localStorage.getItem('wastenet_token') || !localStorage.getItem('wastenet_user')) {
      console.log('No authentication found, setting test user data...');
      // Set a test token
      const testToken = 'test_donor_token_12345';
      localStorage.setItem('wastenet_token', testToken);
      
      // Set a test user with donor role
      const testUser = {
        id: 'test_user_1',
        name: 'Test Donor',
        email: 'testdonor@example.com',
        role: 'donor'
      };
      localStorage.setItem('wastenet_user', JSON.stringify(testUser));
      // Reload the page to use new auth data
      window.location.reload();
    }
  }, []);
  
  useEffect(() => {
    console.log('Dashboard useEffect triggered');
    console.log('Current user:', user);
    console.log('User auth token:', localStorage.getItem('wastenet_token'));
    console.log('User role from localStorage:', localStorage.getItem('wastenet_user') ? JSON.parse(localStorage.getItem('wastenet_user')).role : 'No user data');
    
    // Fetch dashboard data using our API services
    const fetchDashboardData = async () => {
      console.log('Fetching dashboard data...');
      setIsLoading(true);
      try {
        // Fetch data based on user role
        if (user?.role === 'donor') {
          console.log('User is a donor, fetching donor data...');
          
          let listingsResponse = { listings: [] };
          let requestsResponse = { requests: [] };
          let notificationsResponse = { notifications: [] };
          
          try {
            console.log('Calling listingService.getMyListings()...');
            listingsResponse = await listingService.getMyListings();
            console.log('Listings API call successful:', listingsResponse);
          } catch (error) {
            console.error('Error fetching listings:', error);
          }
          
          try {  
            console.log('Calling requestService.getMyDonorRequests()...');
            requestsResponse = await requestService.getMyDonorRequests();
            console.log('Requests API call successful:', requestsResponse);
          } catch (error) {
            console.error('Error fetching requests:', error);
          }
          
          try {
            console.log('Calling notificationService.getNotifications()...');
            notificationsResponse = await notificationService.getNotifications(1, 5);
            console.log('Notifications API call successful:', notificationsResponse);
          } catch (error) {
            console.error('Error fetching notifications:', error);
          }
          
          // Debug the response structures
          console.log('Listings structure:', listingsResponse);
          console.log('Requests structure:', requestsResponse);
          
          // Handle different possible response structures
          // For listings - handle both nested and direct formats
          let listings = [];
          if (listingsResponse.listings?.results) {
            // Handle Cloudflare Workers nested structure
            listings = listingsResponse.listings.results;
          } else if (Array.isArray(listingsResponse.listings)) {
            listings = listingsResponse.listings;
          } else if (listingsResponse.results) {
            // Direct results object
            listings = listingsResponse.results;
          }
            
          // For requests - handle nested structure
          let requests = [];
          if (requestsResponse.requests?.results) {
            // Handle Cloudflare Workers nested structure
            requests = requestsResponse.requests.results;
          } else if (Array.isArray(requestsResponse.requests)) {
            requests = requestsResponse.requests;
          } else if (requestsResponse.results) {
            // Direct results object
            requests = requestsResponse.results;
          }
          
          console.log('Parsed listings:', listings);
          console.log('Parsed requests:', requests);
          
          // Create recent activity list from listings and requests
          const recentItems = [
            ...listings.map(listing => ({
              id: `listing-${listing.id}`,
              type: 'listing',
              title: listing.title || 'Food Listing',
              status: listing.status,
              date: listing.created_at || listing.createdAt || new Date().toISOString(),
              url: `/listings/${listing.id}`
            })),
            ...requests.map(request => ({
              id: `request-${request.id}`,
              type: 'request',
              title: request.listing_title || 'Food Request',
              status: request.status,
              date: request.created_at || request.createdAt || new Date().toISOString(),
              url: `/requests/${request.id}`
            }))
          ];
          
          // Sort by date
          recentItems.sort((a, b) => new Date(b.date) - new Date(a.date));
          setRecentActivity(recentItems.slice(0, 5));
          
          // Calculate metrics
          const activeListings = listings.filter(l => l.status === 'available')?.length || 0;
          const pendingRequests = requests.filter(r => r.status === 'pending')?.length || 0;
          const completedDonations = requests.filter(r => r.status === 'fulfilled' || r.status === 'approved')?.length || 0;
          
          // Calculate total impact (kg of food saved)
          const totalImpact = requests
            .filter(r => r.status === 'fulfilled' || r.status === 'approved')
            .reduce((total, request) => {
              // Try different property paths for quantity
              const quantity = parseFloat(request.quantity) || 
                             parseFloat(request.listing_quantity) || 
                             (request.listing && parseFloat(request.listing.quantity)) || 0;
              return total + quantity;
            }, 0);
          
          setStats({
            activeListings,
            pendingRequests,
            completedDonations,
            totalImpact: totalImpact.toFixed(1) // Format to one decimal place
          });
          
          // Format and combine notifications with other activity
          let notifications = [];
          
          if (notificationsResponse.notifications?.results) {
            notifications = notificationsResponse.notifications.results;
          } else if (Array.isArray(notificationsResponse.notifications)) {
            notifications = notificationsResponse.notifications;
          } else if (notificationsResponse.results) {
            notifications = notificationsResponse.results;
          }
          
          const notificationItems = notifications.map(notification => ({
            id: notification.id,
            type: notification.type || 'notification',
            title: notification.message || 'Notification',
            date: notification.created_at || new Date().toISOString(),
            status: notification.read ? 'read' : 'unread'
          })) || [];
          
          // Combine with other activity items
          if (recentItems.length === 0) {
            setRecentActivity(notificationItems);
          } else {
            // Combine and resort
            const allActivity = [...recentItems, ...notificationItems];
            allActivity.sort((a, b) => new Date(b.date) - new Date(a.date));
            setRecentActivity(allActivity.slice(0, 5));
          }
        } else {
          // For recipients, get available listings and their requests with error handling
          let listingsResponse = { listings: [] };
          let requestsResponse = { requests: [] };
          let notificationsResponse = { notifications: [] };
          
          try {
            // Get all available listings
            listingsResponse = await listingService.getAllListings();
          } catch (error) {
            console.error('Error fetching listings:', error);
            // Use console.error instead of toast to prevent further errors
            console.error('Failed to load available listings');
          }
          
          try {
            // Get recipient's requests
            requestsResponse = await requestService.getMyRecipientRequests();
          } catch (error) {
            console.error('Error fetching recipient requests:', error);
            // Use console.error instead of toast to prevent further errors
            console.error('Failed to load your requests');
          }
          
          try {
            // Get notifications
            notificationsResponse = await notificationService.getNotifications(1, 5);
          } catch (error) {
            console.error('Error fetching notifications:', error);
          }
          
          // Calculate metrics with null checks
          const activeRequests = requestsResponse?.requests?.filter(r => r.status === 'pending')?.length || 0;
          const acceptedRequests = requestsResponse?.requests?.filter(r => r.status === 'accepted')?.length || 0;
          const receivedDonations = requestsResponse?.requests?.filter(r => r.status === 'fulfilled')?.length || 0;
          const availableListings = listingsResponse?.listings?.filter(l => l.status === 'available')?.length || 0;
          
          setStats({
            activeRequests,
            acceptedRequests,
            receivedDonations,
            availableListings
          });
          
          // Format notifications as activity items
          const activity = notificationsResponse.notifications?.map(notification => ({
            id: notification.id,
            type: notification.type || 'notification',
            title: notification.message || 'Notification',
            date: new Date(notification.created_at).toLocaleDateString(),
            status: notification.read ? 'read' : 'unread'
          })) || [];
          
          setRecentActivity(activity);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, [user]);

  // Render a welcome message based on time of day
  const getWelcomeMessage = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  // Render statistics cards based on user role
  const renderStatsCards = () => {
    if (user?.role === 'donor') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard 
            title="Active Listings" 
            value={stats.activeListings} 
            icon="📦"
            color="bg-blue-100" 
            linkTo="/listings/manage" 
          />
          <StatCard 
            title="Pending Requests" 
            value={stats.pendingRequests} 
            icon="⏳"
            color="bg-yellow-100" 
            linkTo="/requests" 
          />
          <StatCard 
            title="Completed Donations" 
            value={stats.completedDonations} 
            icon="✅"
            color="bg-green-100" 
            linkTo="/history" 
          />
          <StatCard 
            title="Total Impact (kg)" 
            value={stats.totalImpact} 
            icon="🌍"
            color="bg-purple-100" 
            linkTo="/impact" 
          />
        </div>
      );
    } else {
      // Recipient view
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard 
            title="Active Requests" 
            value={stats.activeRequests} 
            icon="🔍"
            color="bg-blue-100" 
            linkTo="/requests/manage" 
          />
          <StatCard 
            title="Accepted Requests" 
            value={stats.acceptedRequests} 
            icon="👍"
            color="bg-green-100" 
            linkTo="/requests/accepted" 
          />
          <StatCard 
            title="Received Donations" 
            value={stats.receivedDonations} 
            icon="🎁"
            color="bg-purple-100" 
            linkTo="/history" 
          />
          <StatCard 
            title="Available Listings" 
            value={stats.availableListings} 
            icon="📋"
            color="bg-yellow-100" 
            linkTo="/listings" 
          />
        </div>
      );
    }
  };

  // Component for individual stat card
  const StatCard = ({ title, value, icon, color, linkTo }) => (
    <Link to={linkTo} className={`${color} rounded-lg p-4 shadow-sm transition-transform hover:scale-105`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900">{value}</p>
        </div>
        <span className="text-2xl">{icon}</span>
      </div>
    </Link>
  );

  // Component for activity item
  const ActivityItem = ({ item }) => {
    const getStatusColor = (status) => {
      switch (status) {
        case 'pending': return 'bg-yellow-100 text-yellow-800';
        case 'completed': return 'bg-green-100 text-green-800';
        case 'expired': return 'bg-gray-100 text-gray-800';
        case 'new': return 'bg-blue-100 text-blue-800';
        case 'accepted': return 'bg-green-100 text-green-800';
        case 'scheduled': return 'bg-purple-100 text-purple-800';
        default: return 'bg-gray-100 text-gray-800';
      }
    };

    return (
      <div className="flex items-center py-3 border-b border-gray-200 last:border-0">
        <div className="mr-4 flex-shrink-0">
          {item.type === 'request' ? (
            <span className="p-2 bg-blue-100 rounded-full">🔍</span>
          ) : item.type === 'listing' ? (
            <span className="p-2 bg-green-100 rounded-full">📦</span>
          ) : (
            <span className="p-2 bg-purple-100 rounded-full">🎁</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{item.title}</p>
          <p className="text-xs text-gray-500">{item.date}</p>
        </div>
        <div className="ml-2">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
            {item.status}
          </span>
        </div>
      </div>
    );
  };

  // Quick action buttons based on user role
  const renderQuickActions = () => {
    if (user?.role === 'donor') {
      return (
        <>
          <Link 
            to="/create-listing" 
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Create New Listing
          </Link>
          <Link 
            to="/requests/donor" 
            className="ml-3 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            View Requests
          </Link>
        </>
      );
    } else {
      // Recipient view
      return (
        <>
          <Link 
            to="/listings" 
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Browse Available Food
          </Link>
          <Link 
            to="/requests/manage" 
            className="ml-3 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Manage Requests
          </Link>
        </>
      );
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {getWelcomeMessage()}, {user?.name || 'User'}
          </h1>
          <p className="text-gray-600 mt-1">
            {user?.role === 'donor' 
              ? 'Manage your food listings and requests' 
              : 'Find available food and manage your requests'}
          </p>
        </div>
        <div className="mt-4 sm:mt-0 space-x-3 flex flex-wrap gap-y-2">
          {renderQuickActions()}
        </div>
      </div>

      {/* Stats */}
      {renderStatsCards()}

      {/* Recent Activity */}
      <div className="bg-white shadow rounded-lg p-4 mt-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Recent Activity</h2>
        <div className="space-y-1">
          {recentActivity.length > 0 ? (
            recentActivity.map(item => (
              <ActivityItem key={item.id} item={item} />
            ))
          ) : (
            <p className="text-gray-500 text-sm py-4">No recent activity</p>
          )}
        </div>
        <div className="mt-4 text-right">
          <Link 
            to="/activity" 
            className="text-primary-600 hover:text-primary-700 text-sm font-medium"
          >
            View all activity →
          </Link>
        </div>
      </div>

      {/* Role-specific content */}
      {user?.role === 'donor' ? (
        <div className="bg-white shadow rounded-lg p-4 mt-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Food Donation Tips</h2>
          <ul className="list-disc pl-5 space-y-2">
            <li className="text-gray-600">Make sure to clearly describe the food type, quantity, and expiry date</li>
            <li className="text-gray-600">Upload clear photos of the food items to help recipients</li>
            <li className="text-gray-600">Specify pickup hours and any special instructions</li>
            <li className="text-gray-600">Always ensure food safety guidelines are followed</li>
          </ul>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg p-4 mt-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Nearby Food Available</h2>
          <div className="h-64 bg-gray-100 flex items-center justify-center rounded">
            <p className="text-gray-500">Map view will display nearby available food</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardPage;
